<template>
  <div class="row">
    <div class="col-md-12">
      <div class="ibox">
        <div class="ibox-title">
          <h1>Import USAW information</h1>
        </div>
        <div class="ibox-content">
          <div v-if="!verificationEnabled" class="alert alert-warning" data-cy="usaw-import-offline">
            USA Wrestling verification is turned off right now, so imports are paused. Please try again later.
          </div>
          <template v-else>
          <div v-if="showUploadForm()">
            <div>
              <h2>Upload the .csv export from USAW</h2>
              <p>
                Your .csv file should come directly from USAW. The following column names are required for the import to work.
              </p>
              <p>
                Each membership number is verified with USA Wrestling as it is imported, so the membership type and
                expiration come from USA Wrestling. Numbers that USA Wrestling reports as inactive are skipped.
              </p>
              <ul>
                <li>usaw_id</li>
                <li>first_name</li>
                <li>last_name</li>
                <li>dob</li>
              </ul>
            </div>
            <div class="m-t-md">
              <input type="file" id="files">
              <ladda-button @lbClicked="parseFile" el-class="btn-primary m-t-lg" :loading="fileParsing">
                Upload
              </ladda-button>
              <div v-if="error">
                <small class="text-danger">
                  {{ errorMessage }}
                </small>
              </div>
            </div>
          </div>
          <div v-if="!showUploadForm()">
            <div>
              <h2>
                Verify Headers
              </h2>
            </div>
            <div class="alert-wrappers">
              <div v-if="validFieldsFound.length > 0" class="alert alert-success">
                <strong>{{ arrayToStr(validFieldsFound) }}</strong> found in file.
              </div>
              <div v-if="ignoredFields.length > 0" class="alert alert-warning">
                <strong>{{ arrayToStr(ignoredFields) }}</strong> found in file, but is not used in the import. These columns are being ignored.
              </div>
              <div v-if="missingOptionalFields.length > 0" class="alert alert-info">
                <strong>{{ arrayToStr(missingOptionalFields) }}</strong> not found in file. This is ok, as this is optional info, but if you expected it to be included, please double check your headers.
              </div>
              <p>
                Want to choose a new file? <strong><a class="text-danger" @click.prevent="reuploadFile()">Click here</a></strong>
              </p>
            </div>
            <div class="m-t-xl">
              <h2>Confirm USAW ids to import</h2>
            </div>
            <div class="uploaded-info list-results">
              <div class="search-result teammate" v-for="row in parsedRows">
                <div class='hr-line-dashed'></div>
                <div class="grid grid-cols-2 sm-grid-cols-1 gap-3">
                  <div>
                    <h3>
                      {{ row.first_name }} {{ row.last_name }}
                    </h3>
                    <p class="search-type-label" v-html="usawInfo(row)"></p>
                  </div>
                  <div class="text-right t-l-md" v-html="importStatus(row)"></div>
                </div>
              </div>
            </div>
            <div>
              <p v-if="!loading">
                <strong>You are about to import {{ toImportCount }} USAW memberships</strong>
              </p>
              <div v-if="loading" class="alert alert-info" data-cy="usaw-import-progress">
                <strong>Importing {{ importedCount }} of {{ importTotal }}&hellip;</strong>
                Each membership is verified with USA Wrestling one at a time, so this takes about
                {{ estimatedMinutes }}. Please keep this tab open until it finishes.
              </div>
              <div v-show="!importFinished">
                <ladda-button @lbClicked="beginImport" el-class="btn-primary m-t-lg" :loading="loading">
                  Import
                </ladda-button>
              </div>
              <div v-show="importFinished">
                {{ importMessage }}
              </div>
            </div>
          </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Spinner from './spinner.vue';
import LaddaButton from '../../shared/ladda_button.vue';
import { importerMix } from './mixins/importer_mix';
import { errorableMix } from './mixins/errorable_mix';

// With USAW API verification on, every membership create is a live lookup against
// USA Wrestling, whose bucket is 60 lookups a minute shared with registrations and
// the nightly recheck sweep. Imports therefore run one row at a time with this gap
// (~30/minute), leaving half the bucket for everyone else.
const IMPORT_SPACING_MS = 2000;
// A 503 means USAW is down or rate limiting us. Wait this long before the next row,
// and give up on the rest of the file after this many in a row.
const UNAVAILABLE_BACKOFF_MS = 60000;
const MAX_CONSECUTIVE_UNAVAILABLE = 5;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default {
  name: 'bulk-add-usaw',
  components: { Spinner, LaddaButton },
  mixins: [importerMix, errorableMix],
  props: {
    // false under USAW_API_MODE=off: the server would fall back to manual entry,
    // which needs an expiration and membership type this page no longer collects
    verificationEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      allFields: ['usaw_id', 'first_name', 'last_name', 'dob'],
      requiredFields: ['usaw_id', 'first_name', 'last_name', 'dob'],
      queryingWrestlers: false,
      importedCount: 0,
      importTotal: 0,
    };
  },
  computed: {
    membershipsToImport() {
      return _.filter(this.parsedRows, { import_status: 'not_started' });
    },
    toImportCount() {
      return this.membershipsToImport.length;
    },
    estimatedMinutes() {
      const seconds = Math.ceil((this.importTotal * IMPORT_SPACING_MS) / 1000);
      if (seconds < 90) {
        return 'a minute';
      }
      return `${Math.ceil(seconds / 60)} minutes`;
    },
  },
  methods: {
    formatUploadedRow(uploaded_row) {
      let { dob } = uploaded_row;
      if (dob) {
        dob = moment(dob, 'MM/DD/YYYY').format('YYYY-MM-DD');
      }
      return {
        usaw_id: uploaded_row.usaw_id,
        first_name: uploaded_row.first_name,
        last_name: uploaded_row.last_name,
        dob,
        import_status: 'searching',
        import_reason: 'Finding wrestler...',
      };
    },
    uploadParsed() {
      const promises = [];
      this.parsedRows.forEach(async (row, index) => {
        promises.push(this.searchForWrestler(row, index));
      });

      Promise.all(promises).then((results) => {
        console.log('all finished');
        console.log(results);
      }).catch((error) => {
        console.log('error');
        console.log(error);
      });
    },
    async searchForWrestler(row, index) {
      console.log(`search for wrestler called with index ${index}`);
      const url = `${this.$apiService.wrestlersUrl()}?first_name=${row.first_name}&last_name=${row.last_name}&dob=${row.dob}`;
      return axios.get(url)
        .then((response) => {
          if (response.data.wrestlers.length > 1) {
            row.import_status = 'duplicate';
            const urls = _.map(response.data.wrestlers, (wp) => `<a href="/wrestlers/${wp.id}" target="_blank"> Profile ${wp.id} <i class="fa-solid fa-up-right-from-square"></i></a>`);
            row.import_reason = `Multiple wrestlers found with this name and dob, skipping.<br/> ${urls.join(',')}`;
          } else if (response.data.wrestlers.length === 1) {
            const wp = response.data.wrestlers[0];
            // The same number already on file and still inside its membership year has
            // nothing to gain from another USAW lookup, so it does not spend one.
            if (wp.usaw_membership_id === row.usaw_id && moment(wp.usaw_expires_on).isSameOrAfter(moment(), 'day')) {
              row.import_status = 'blocked';
              row.import_reason = `USAW membership ${wp.usaw_membership_id} is already on file through ${wp.usaw_expires_on}, skipping`;
            } else {
              row.wrestler_profile_id = wp.id;
              row.import_status = 'not_started';
              row.import_reason = `Wrestler found, ready to verify and import usaw id ${row.usaw_id}`;
            }
          } else {
            row.import_status = 'blocked';
            row.import_reason = `No wrestler found with name ${row.first_name} ${row.last_name} and dob of ${row.dob}, skipping`;
          }
        })
        .catch((error) => {
          row.import_status = 'failed';
          row.import_reason = 'Searching for wrestler failed';
        });
    },
    usawInfo(row) {
      return `Born on ${row.dob}, attempting to import USAW id ${row.usaw_id}`;
    },
    importStatus(row) {
      switch (row.import_status) {
        case 'searching':
          return `<p><i class='fa-solid fa-magnifying-glass'></i> ${row.import_reason}</p>`;
        case 'not_started':
          return `<p><i class='fa-regular fa-circle'></i> ${row.import_reason}</p>`;
        case 'importing':
          return "<p><i class='fa-solid fa-circle-notch'></i> Importing...</p>";
        case 'blocked':
          return `<p class='text-warning'><i class='fa-solid fa-triangle-exclamation'></i> ${row.import_reason}</p>`;
        case 'duplicate':
          return `<p class='text-danger'><i class='fa-solid fa-circle-xmark'></i> ${row.import_reason}</p>`;
        case 'failed':
          return `<p class='text-danger'><i class='fa-solid fa-circle-xmark'></i> ${row.import_reason}</p>`;
        case 'imported':
          return `<p class='text-success'><i class='fa-regular fa-circle-check'></i> ${row.import_reason}</p>`;
      }
    },
    // Resolves (never rejects) with the HTTP status, so the sequential loop can
    // decide whether to back off. Row state is updated in place for the list.
    createUsawMembership(row) {
      const vm = this;
      const url = vm.$apiService.usawMemberships(row.wrestler_profile_id);
      // Verified server side: USAW supplies the membership type and expiration
      const params = { usaw_membership: { public_id: row.usaw_id } };
      row.import_status = 'importing';
      return axios.post(url, params)
        .then(() => {
          row.import_status = 'imported';
          row.import_reason = `Usaw id ${row.usaw_id} verified and imported`;
          return 201;
        })
        .catch((error) => {
          const status = error.response?.status;
          row.import_status = 'failed';
          row.import_reason = status === 429
            ? 'Too many lookups in a short time, skipped. Re-upload the file in a few minutes to retry.'
            : vm.parseErrorResponse(error);
          return status || 0;
        });
    },
    // One row at a time, IMPORT_SPACING_MS apart, so a big file never bursts the
    // USAW quota. Rows that never got a turn (USAW down) are left as 'not_started'
    // so re-uploading the file picks them up.
    async beginImport() {
      const vm = this;
      if (vm.loading) {
        return;
      }

      const rows = vm.membershipsToImport;
      vm.loading = true;
      vm.importedCount = 0;
      vm.importTotal = rows.length;
      let unavailableStreak = 0;

      // Sequential on purpose (see IMPORT_SPACING_MS); parallel is exactly the problem
      /* eslint-disable no-await-in-loop */
      for (let i = 0; i < rows.length; i += 1) {
        if (i > 0) {
          await sleep(unavailableStreak > 0 ? UNAVAILABLE_BACKOFF_MS : IMPORT_SPACING_MS);
        }
        const status = await vm.createUsawMembership(rows[i]);
        vm.importedCount = i + 1;

        if (status === 503 || status === 429) {
          unavailableStreak += 1;
          if (unavailableStreak >= MAX_CONSECUTIVE_UNAVAILABLE) {
            vm.importMessage = `Stopped after ${MAX_CONSECUTIVE_UNAVAILABLE} USA Wrestling errors in a row. `
              + `${rows.length - vm.importedCount} memberships were not attempted; re-upload the file later to retry them.`;
            break;
          }
        } else {
          unavailableStreak = 0;
        }
      }
      /* eslint-enable no-await-in-loop */

      vm.loading = false;
      vm.importFinished = true;
      vm.importMessage = vm.importMessage || 'Finished importing';
    },
  },
};
</script>
<style scoped>
</style>
